import * as React from 'react'
import cn from 'classnames'
import { CaretDown, Check, Plus } from '@phosphor-icons/react'
import { NotificationCard } from 'components/checkin-notifications'
import { CycleWorkflow, ParticipantScope } from './types'
import { NotificationDraft } from './notifications'
import { fieldLabelClass, baseInputClass, FieldError } from './form-helpers'

export type { CycleWorkflow }

const SCOPE_OPTIONS: Array<{ value: ParticipantScope; label: string }> = [
  { value: 'whole_org', label: 'Whole organisation' },
  { value: 'teams', label: 'Specific teams' },
]

export const WORKFLOW_OPTIONS: Array<{ value: CycleWorkflow; label: string }> =
  [
    { value: 'default', label: 'Default Check-ins' },
    { value: 'flexible', label: 'Flexible check-in (Join review option)' },
  ]

export const workflowLabel = (workflow: CycleWorkflow): string =>
  WORKFLOW_OPTIONS.find((option) => option.value === workflow)?.label ??
  workflow

export type OrgTeam = { id: string; name: string }

type StepTwoProps = {
  scope: ParticipantScope
  onScopeChange: (scope: ParticipantScope) => void
  teams: OrgTeam[]
  teamsLoading: boolean
  teamsLoadError: string | null
  selectedTeamIds: string[]
  onToggleTeam: (teamId: string) => void
  workflow: CycleWorkflow
  onWorkflowChange: (workflow: CycleWorkflow) => void
  teamsError: string | null
  notifications: NotificationDraft[]
  onToggleNotification: (id: string) => void
  onEditNotification: (notification: NotificationDraft) => void
  onRemoveNotification: (id: string) => void
  onAddNotification: () => void
}

export const StepTwo = ({
  scope,
  onScopeChange,
  teams,
  teamsLoading,
  teamsLoadError,
  selectedTeamIds,
  onToggleTeam,
  workflow,
  onWorkflowChange,
  teamsError,
  notifications,
  onToggleNotification,
  onEditNotification,
  onRemoveNotification,
  onAddNotification,
}: StepTwoProps) => {
  let teamsContent: React.ReactNode
  if (teamsLoading) {
    teamsContent = <p className="text-[15px] text-[#5E6580]">Loading teams…</p>
  } else if (teamsLoadError) {
    teamsContent = (
      <p className="text-[15px] text-[#E42309]">{teamsLoadError}</p>
    )
  } else if (teams.length === 0) {
    teamsContent = (
      <p className="text-[15px] text-[#5E6580]">
        This organisation has no teams yet.
      </p>
    )
  } else {
    teamsContent = (
      <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-8">
        {teams.map((team) => {
          const checked = selectedTeamIds.includes(team.id)

          return (
            <button
              key={team.id}
              type="button"
              onClick={() => onToggleTeam(team.id)}
              aria-pressed={checked}
              className="flex items-center gap-3 text-left"
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-solid transition-colors',
                  checked
                    ? '!border-[#1D264A] bg-[#1D264A]'
                    : '!border-[#C7CCDD] bg-white',
                )}
              >
                {checked && (
                  <Check size={14} weight="bold" className="text-white" />
                )}
              </span>
              <span className="text-[16px] text-[#1D264A]">{team.name}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <div className="mb-6">
        <p className={fieldLabelClass}>Participating Teams</p>
        <div className="flex flex-wrap gap-3">
          {SCOPE_OPTIONS.map((option) => {
            const isActive = scope === option.value

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onScopeChange(option.value)}
                aria-pressed={isActive}
                className={cn(
                  'rounded-full border border-solid px-3 py-2 font-talent text-[16px] font-normal leading-[20px] transition-colors',
                  isActive
                    ? '!border-[#2E9E5D] bg-[#2E9E5D] text-white'
                    : '!border-[#D7DBE8] bg-white text-[#1D264A] hover:!border-[#2E9E5D]',
                )}
              >
                {option.label}
              </button>
            )
          })}
        </div>

        {scope === 'teams' && (
          <div className="mt-3 rounded-lg bg-[#F7F8FB] p-5">
            {teamsContent}
            <FieldError message={teamsError} />
          </div>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="workflow" className={fieldLabelClass}>
          Workflow
        </label>
        <div className="relative">
          <select
            id="workflow"
            value={workflow}
            onChange={(event) =>
              onWorkflowChange(event.target.value as CycleWorkflow)
            }
            className={cn(
              baseInputClass,
              'appearance-none pr-11 focus:ring-[#2E9E5D]',
            )}
          >
            {WORKFLOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <CaretDown
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
            size={18}
          />
        </div>
      </div>

      <div>
        <p className={cn(fieldLabelClass, 'mb-2')}>
          Email notification cadence
        </p>
        <div className="flex flex-col gap-3">
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onToggle={() => onToggleNotification(notification.id)}
              onEdit={() => onEditNotification(notification)}
              onRemove={() => onRemoveNotification(notification.id)}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={onAddNotification}
          className="mt-4 inline-flex items-center gap-2 font-talent text-[15px] font-medium leading-[18px] text-[#1D264A]"
        >
          <Plus size={18} weight="bold" />
          Add another reminder
        </button>
      </div>
    </>
  )
}
